import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why only 5 numbers? Aren\'t there more important metrics?',
    a: 'Dividend investors get overwhelmed checking 20 metrics and end up checking none. Five is the number you can actually remember and run through mentally in under 60 seconds. The five chosen here covers ~85% of the risk surface for typical dividend buys.',
  },
  {
    q: 'What\'s the order of importance?',
    a: 'For most stocks: FCF coverage > payout ratio > dividend streak > debt/EBITDA > sector context. For REITs and BDCs, swap FCF coverage for AFFO/NII coverage. For utilities, sector context moves up because the regulatory environment dominates.',
  },
  {
    q: 'Where do I find these numbers in DiviDrip?',
    a: 'Four of the five are visible in DiviDrip. Payout Ratio is on the Dividend Info tab. The dividend streak shows up inside the Dividend Tier card on the Dividend Info tab and on the dedicated Triangle tab. Free Cash Flow and FCF Margin are on the Stock Metrics tab — compute coverage manually against the Annual Dividend shown on the Dividend Info tab. Debt-related metrics on the Stock Metrics tab include Debt/Equity, Total Debt and Total Cash (Debt/EBITDA itself isn\'t shown — use Debt/Equity as a quick leverage proxy). Sector appears in the modal header metadata and in the peer-compare popouts.',
  },
  {
    q: 'What if a stock fails on one of the five?',
    a: 'One failure = pause, investigate, decide. Two failures = caution, only consider if you have a strong thesis. Three+ failures = walk away. There are too many other quality dividend payers to bend the rules on a single questionable name.',
  },
  {
    q: 'How is this different from the Dividend Triangle score?',
    a: 'The Triangle score is automated and gives you a single number. The 5-number check is a manual sanity check you do BEFORE pulling the trigger on a buy. Use Triangle to filter for candidates from a universe of 4,500 names; use the 5-number check to validate a specific candidate before clicking Buy.',
  },
];

export default function LearnFiveNumbers() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The 5 Numbers I Check Before Buying Any Dividend Stock (2026) | DiviDrip"
      description="A repeatable 60-second checklist for evaluating any dividend stock: FCF coverage, payout ratio, dividend streak, debt/EBITDA, sector context. Memorable, fast, and catches 85% of bad buys."
      canonical="https://twylightcrow.com/learn/five-numbers-to-check"
      guidePath="/learn/five-numbers-to-check"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · Buy checklist"
      h1="The 5 Numbers I Check Before Any Dividend Buy"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
      ]}
    >
      <p>
        Most "how to pick dividend stocks" articles list 25 metrics. I've never met
        an investor who actually checks 25 metrics. So here's a list of 5 that fit
        in my head and have caught every bad buy I almost made.
      </p>

      <aside className="not-prose my-6 rounded-lg border border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/30 p-4">
        <p className="text-sm font-semibold text-sky-900 dark:text-sky-200 mb-1">
          Picking an ETF instead of a stock?
        </p>
        <p className="text-sm text-sky-900/85 dark:text-sky-200/85">
          The 5 numbers below assume you&apos;re evaluating a single-name
          stock with its own revenue, EPS, and FCF. ETFs don&apos;t have
          those — for ETFs use expense ratio, AUM, distribution growth,
          and holdings. See{' '}
          <Link to="/learn/evaluating-dividend-etfs" className="underline">
            How to Evaluate Dividend ETFs
          </Link>
          {' '}for the 5 numbers that DO apply to funds.
        </p>
      </aside>

      <h2>#1 — FCF Coverage Ratio</h2>
      <p>
        <strong>Formula:</strong> Free Cash Flow ÷ Dividends Paid<br />
        <strong>Target:</strong> Above 1.5 (i.e. FCF is at least 150% of dividends paid)
      </p>
      <p>
        FCF is the cash actually available after operating expenses and capex. If a
        company's dividends consume more than 80% of their FCF, every bad quarter
        becomes a threat. Below 100% means the dividend is being subsidised by debt
        or asset sales — eventually unsustainable.
      </p>
      <p>
        For REITs, substitute AFFO Coverage. For BDCs, substitute NII Coverage.
      </p>

      <h2>#2 — Payout Ratio</h2>
      <p>
        <strong>Formula:</strong> Dividends Paid ÷ Net Income<br />
        <strong>Target:</strong> Under 60% for standard corps, under 80% for utilities, under 90% AFFO for REITs, under 100% NII for BDCs
      </p>
      <p>
        Payout ratio = how much earnings room the company has for dividend hikes
        without stretching. Low payout = lots of room to grow + safety cushion in
        downturns. High payout = the next hike will be small or non-existent, and a
        bad year forces a cut.
      </p>
      <p>
        Watch payout ratio TREND more than the absolute number. A payout ratio of
        65% that's been stable for a decade is fine; 65% that just jumped from 40%
        last year is a warning.
      </p>

      <h2>#3 — Dividend Streak (Years of Consecutive Raises)</h2>
      <p>
        <strong>Target:</strong> At least 5 years, ideally 10+. 25+ = Aristocrat. 50+ = King.
      </p>
      <p>
        Streak length is the simplest measure of management commitment to the
        dividend. A 25-year streak survived 2008-09, the dot-com crash, COVID, and
        every other recent shock. A 2-year streak has been tested by exactly nothing.
      </p>
      <p>
        Streaks also create internal momentum: management measures their performance
        partly by the streak, so they'll fight harder to maintain a 30-year streak
        than a 3-year one. Quality begets quality.
      </p>

      <h2>#4 — Debt-to-EBITDA</h2>
      <p>
        <strong>Formula:</strong> Total Debt ÷ Earnings Before Interest, Taxes, Depreciation, and Amortisation<br />
        <strong>Target:</strong> Under 3.0 for standard corps, under 4.0 for utilities, varies by REIT/BDC structure
      </p>
      <p>
        Leverage check. A company with 5x+ debt-to-EBITDA is one interest-rate cycle
        from a covenant violation. Dividend cuts happen in lockstep with debt-load
        crises far more often than from pure business underperformance.
      </p>
      <p>
        Watch for sudden increases. A company that's been at 2x for years and
        suddenly jumps to 4x via an acquisition has changed risk profile dramatically
        — even if the dividend hasn't moved.
      </p>

      <h2>#5 — Sector Context</h2>
      <p>
        <strong>Target:</strong> The yield is in the normal range for the sector
      </p>
      <p>
        Not a single number, but a sanity check. Different sectors have different
        normal yield ranges:
      </p>
      <ul>
        <li><strong>Tech:</strong> 0.5-2.5% (anything 5%+ usually means the stock crashed)</li>
        <li><strong>Healthcare:</strong> 1.5-4%</li>
        <li><strong>Consumer Staples:</strong> 2.5-4.5%</li>
        <li><strong>Industrials:</strong> 1.5-3.5%</li>
        <li><strong>Financials (banks):</strong> 2-5%</li>
        <li><strong>Utilities:</strong> 3-5%</li>
        <li><strong>REITs:</strong> 3-7% (mortgage REITs 8-12%)</li>
        <li><strong>BDCs:</strong> 7-12%</li>
        <li><strong>Energy (XOM/CVX):</strong> 3-5%</li>
      </ul>
      <p>
        If a stock's yield is dramatically higher than its sector average, ask why.
        Usually one of: (1) the stock dropped sharply on recent news, (2) the
        dividend is unsustainably high, (3) the company is in transition. None of
        these are automatically disqualifying — but you need to KNOW which one
        before buying.
      </p>

      <h2>The 60-second mental routine</h2>
      <ol>
        <li>FCF covers dividend at &gt; 1.5x? ✓ / ✗</li>
        <li>Payout ratio within sector norms? ✓ / ✗</li>
        <li>Streak of 5+ years of raises? ✓ / ✗</li>
        <li>Debt/EBITDA at sector-typical levels? ✓ / ✗</li>
        <li>Yield within normal sector range? ✓ / ✗</li>
      </ol>
      <p>
        5 ✓ = buy with conviction. 4 ✓ + 1 ? = buy with smaller position size. 3 ✓
        = pause, investigate, write down your thesis. 2 or fewer = walk away.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Run the check in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and click any ticker to launch
        the Stock Modal. <strong>Payout Ratio</strong> is on the Dividend Info tab,
        the <strong>dividend streak</strong> shows up inside the Dividend Tier card
        on the Dividend Info tab (and on the dedicated Triangle tab), and{' '}
        <strong>Free Cash Flow</strong> + FCF Margin live on the Stock Metrics tab —
        you compute coverage manually against the Annual Dividend shown on the
        Dividend Info tab. The Stock Metrics tab also surfaces Debt/Equity, Total
        Debt and Total Cash for a quick leverage read. Sector context lives in
        the modal header subtitle and in the peer-compare popouts. The Dividend
        Triangle tab is the useful pre-filter; the five-number check is the
        final gate.
      </p>
    </LearnPage>
  );
}
